import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { printExpenses } from "@/lib/print";
import { useSettings } from "@/context/SettingsContext";
import { fmtMoney, fmtDate, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Receipt, Printer } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", amount: "", date: todayISO(), category: "General", notes: "" };

export default function Expenses() {
  const { currency, dateFormat, business, settings } = useSettings();
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/expenses");
    setExpenses(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const amt = parseFloat(form.amount);
    if (!form.title.trim() || !amt) { toast.error("Enter title and amount"); return; }
    setSaving(true);
    try {
      await api.post("/expenses", { ...form, amount: amt });
      toast.success("Expense added");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/expenses/${id}`);
    toast.success("Expense deleted"); load();
  };

  const total = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Expenses</h1>
          <p className="text-slate-500 mt-1">Track business expenses for accurate P&amp;L</p>
        </div>
        <Button className="bg-maroon hover:bg-maroon-hover" onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-expense-button">
          <Plus className="h-4 w-4 mr-2" /> Add Expense
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading font-semibold text-navy">All Expenses</h2>

          <div className="flex items-center gap-3">
            <span className="font-mono font-bold text-red-600">
              Total: {fmtMoney(total, currency)}
            </span>

            <Button
              variant="outline"
              onClick={() =>
                printExpenses(
                  expenses,
                  total,
                  business,
                  settings
                )
              }
            >
              <Printer className="h-4 w-4 mr-2" />
              Print / Save PDF
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-3">Sl. No.</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Category</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.length ? expenses.map((e, index) => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                  <td className="px-5 py-3 font-mono text-slate-500">{index + 1}</td>
                  <td className="px-5 py-3 text-slate-500">{fmtDate(e.date, dateFormat)}</td>
                  <td className="px-5 py-3 font-medium">{e.title}</td>
                  <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">{e.category}</span></td>
                  <td className="px-5 py-3 text-right font-mono text-red-600">{fmtMoney(e.amount, currency)}</td>
                  <td className="px-5 py-3 text-right">
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(e.id)} data-testid={`delete-expense-${e.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">
                  <Receipt className="h-8 w-8 mx-auto text-slate-300 mb-2" />No expenses recorded.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-expense-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Expense</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="expense-title-input" /></div>
            <div><Label>Amount ({currency})</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" /></div>
            <div><Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date-input" /></div>
            <div className="col-span-2"><Label>Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="expense-category-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-maroon hover:bg-maroon-hover" onClick={create} disabled={saving} data-testid="save-expense-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
